import * as React from "react";
import { observer, inject } from "mobx-react";
import MonthChart from "./MonthChart/MonthChart";
import { TripCostEstimatorProps, TripCostEstimatorHotelCost } from "./typings";
import { LinkListView, DIALOG_QUERY } from "./LinkListView/LinkListView";
import TripCostDialog from "./TripCostDialog/TripCostDialog";
import {
  UitkCard,
  UitkCardContent,
  UitkCardTextSegment,
  UitkCardSectionSeparatorType
} from "uitk-react-cards";
import { action, observable } from "mobx";
import {
  sendImmediateTrackEvent,
  Action,
  FlexTrackingInfo
} from "src/components/utility/analytics/FlexAnalyticsUtils";
import { formatText } from "bernie-l10n";
import { Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";

enum EVENT_EMPTY_DATA {
  MONTH = "month.empty",
  RESPONSE = "response.empty"
}

const prevYear = new Date().getFullYear() - 1;

const l10n = {
  title: (localizedName: string) => formatText("tripCostEstimator.title", localizedName),
  footerText: () => formatText("tripCostEstimator.footer", prevYear, "Expedia.com"),
  allPrice: () => formatText("tripCostEstimator.allPrices", "USD"),
  emptyPrice: () => formatText("tripCostEstimator.emptyPrice"),
  dialogTitle: (localizedName: string) =>
    formatText("tripCostEstimator.dialog.title", localizedName),
  viewList: () => formatText("tripCostEstimator.viewList")
};

@inject("analytics", "routeStore")
@observer
export class TripCostEstimator extends React.Component<TripCostEstimatorProps> {
  private moduleContainer = React.createRef<HTMLDivElement>();

  @observable
  private popover: { closePopover: () => void };

  constructor(props: TripCostEstimatorProps) {
    super(props);
    this.closeDialog = this.closeDialog.bind(this);
    this.redirectHSR = this.redirectHSR.bind(this);
    this.sendTrackEmptyData = this.sendTrackEmptyData.bind(this);
    this.handleRenderedGraph = this.handleRenderedGraph.bind(this);
    this.closePopover = this.closePopover.bind(this);

    const { content } = props.flexComponent.model;
    const destinationTrendPrice = content && content.destinationTrendPrice;
    const hasEmptyResponse = destinationTrendPrice && destinationTrendPrice.hasEmptyResponse;
    const hasEmptyMonthAvgPrice =
      destinationTrendPrice && destinationTrendPrice.hasEmptyMonthAvgPrice;

    if (hasEmptyResponse) {
      this.sendTrackEmptyData(EVENT_EMPTY_DATA.RESPONSE);
    }

    if (hasEmptyMonthAvgPrice) {
      this.sendTrackEmptyData(EVENT_EMPTY_DATA.MONTH);
    }
  }

  public sendTrackEmptyData(rfrr: string) {
    const { analytics } = this.props;
    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: "tripCostEstimator",
      rfrr,
      action: Action.IMPRESSION
    };

    sendImmediateTrackEvent(flexTrackingInfo, analytics);
  }

  @action
  public closeDialog() {
    const { analytics, routeStore } = this.props;
    if (routeStore) {
      routeStore.closeDialog(DIALOG_QUERY.NAME);

      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: "tripCostEstimator",
        rfrr: "overlay.close",
        action: Action.CLICK
      };

      sendImmediateTrackEvent(flexTrackingInfo, analytics);
    }
  }

  public redirectHSR(hotelCost?: TripCostEstimatorHotelCost) {
    if (hotelCost) {
      location.href = hotelCost.url;
    }
  }

  @action
  public handleRenderedGraph(popover: { closePopover: () => void }) {
    this.popover = popover;
  }

  @action
  public closePopover() {
    if (this.popover) {
      this.popover.closePopover();
    }
  }

  public render() {
    const flexComponent = this.props.flexComponent;
    const showPopover = Boolean(flexComponent.model && flexComponent.model.showPopover);
    const destinationTrendPrice =
      flexComponent.model.content && flexComponent.model.content.destinationTrendPrice;
    const hasEmptyResponse = destinationTrendPrice && destinationTrendPrice.hasEmptyResponse;
    const hasVisibleEmptyMonthAvgPrice =
      destinationTrendPrice && destinationTrendPrice.hasVisibleEmptyMonthAvgPrice;
    const hotelCostByStars =
      (destinationTrendPrice && destinationTrendPrice.hotelCostByStars) || [];

    if (hasEmptyResponse || !hotelCostByStars.length) {
      return null;
    }

    const cheapestMonthCost =
      destinationTrendPrice &&
      destinationTrendPrice.cheapestMonth &&
      destinationTrendPrice.cheapestMonth.cost;
    const expensiveMonthCost =
      destinationTrendPrice &&
      destinationTrendPrice.expensiveMonth &&
      destinationTrendPrice.expensiveMonth.cost;

    const localizedName = this.props.context.searchContext.location.localizedName;

    const defaultTitle = l10n.title(localizedName);
    const defaultDialogTitle = l10n.dialogTitle(localizedName);

    const Dialog = (
      <TripCostDialog
        title={defaultDialogTitle}
        onClickList={this.redirectHSR}
        close={this.closeDialog}
        hotelCostList={hotelCostByStars}
      />
    );

    const keyHelper = new ItemKeyHelper("link");
    const seoLinks = hotelCostByStars.map(hotelCost => (
      <TrackedLink
        key={keyHelper.next()}
        moduleName={this.props.flexComponent.name}
        rfrr="monthChart"
        linkName={hotelCost.month}
        href={hotelCost.url}
      >
        <span>{`${hotelCost.month} $${hotelCost.avgTwoStarsCost}-${
          hotelCost.avgFourStarsCost
        }`}</span>
      </TrackedLink>
    ));

    return (
      <div
        className="TripCostEstimator uitk-cell all-cell-1-1 all-y-padding-three"
        data-fm={flexComponent.fmId}
      >
        <UitkCard>
          <div
            id={flexComponent.id}
            ref={this.moduleContainer}
            onClick={this.closePopover}
            className="uitk-cell"
          >
            <UitkCardContent>
              <UitkCardTextSegment separator={UitkCardSectionSeparatorType.BOTTOM}>
                <Viewport context={this.props.context}>
                  <ViewSmall>
                    <>
                      <h2 className="uitk-type-600 all-b-padding-four">{defaultTitle}</h2>
                      <MonthChart
                        showPopover={showPopover}
                        id={`graph-${flexComponent.id}`}
                        data={hotelCostByStars}
                        low={cheapestMonthCost}
                        high={expensiveMonthCost}
                        onClick={this.redirectHSR}
                        handleRenderedGraph={this.handleRenderedGraph}
                        isMobile={true}
                      />
                    </>
                  </ViewSmall>
                  <ViewLarge>
                    <>
                      <h2 className="uitk-type-700 all-b-padding-four">{defaultTitle}</h2>
                      <MonthChart
                        showPopover={showPopover}
                        id={`graph-${flexComponent.id}`}
                        data={hotelCostByStars}
                        low={cheapestMonthCost}
                        high={expensiveMonthCost}
                        onClick={this.redirectHSR}
                        handleRenderedGraph={this.handleRenderedGraph}
                        isMobile={false}
                      />
                    </>
                  </ViewLarge>
                </Viewport>
                {hasVisibleEmptyMonthAvgPrice && (
                  <p className="uitk-type-200">{l10n.emptyPrice()}</p>
                )}

                <p className="uitk-type-200">{l10n.footerText()}</p>
                <p className="uitk-type-200">{l10n.allPrice()}</p>
              </UitkCardTextSegment>
              <UitkCardTextSegment>
                <LinkListView text={l10n.viewList()}>{Dialog}</LinkListView>
                <div className="is-visually-hidden">{seoLinks}</div>
              </UitkCardTextSegment>
            </UitkCardContent>
          </div>
        </UitkCard>
      </div>
    );
  }
}

export default TripCostEstimator;
